import { MapCanvas, MapContext } from '@stormwater-software/common';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  buildSiteMapPdf,
  captureMapImage,
  getCaptureSize,
  SiteMapPage,
} from './siteMapPrintPdf';
import { SiteMapPrintData, SiteMapPrintDoc, SiteMapPrintSource } from './siteMapPrintData';
import EsriFeaturesLayer from '../../components/MapEditor/EsriFeaturesLayer';

export interface UseSiteMapPrintOptions {
  source: SiteMapPrintSource;
  projectName: string;
  clientName?: string;
  emptyResultMessage?: string;
  includeEsri?: boolean;
  onComplete: () => void;
}

export interface SiteMapPrintController {
  maps: SiteMapPrintDoc[] | null;
  total: number;
  index: number;
  printing: boolean;
  error: string | null;
  start: () => void;
  /** The hidden DOM where the map is rendered for capture. */
  stage: React.ReactNode;
}

const DEFAULT_EMPTY_RESULT = 'None of these site maps could be printed.';

/**
 * Drives a client-side site map print: resolve the maps, render each one offscreen, capture it,
 * then write the PDF. Any modal can own the UI — it only renders `stage` and calls `start`.
 */
export const useSiteMapPrint = ({
  source,
  projectName,
  clientName,
  emptyResultMessage,
  includeEsri,
  onComplete,
}: UseSiteMapPrintOptions): SiteMapPrintController => {
  const [maps, setMaps] = useState<SiteMapPrintDoc[] | null>(null);
  const [printing, setPrinting] = useState(false);
  const [index, setIndex] = useState(0);
  const [data, setData] = useState<SiteMapPrintData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pages = useRef<SiteMapPage[]>([]);
  const indexRef = useRef(0);
  const mapsRef = useRef<SiteMapPrintDoc[]>([]);
  const dataRef = useRef<SiteMapPrintData | null>(null);
  const captured = useRef(false);
  const sourceRef = useRef(source);
  const optionsRef = useRef({ projectName, clientName, emptyResultMessage, onComplete });

  optionsRef.current = { projectName, clientName, emptyResultMessage, onComplete };
  mapsRef.current = maps || [];
  dataRef.current = data;

  useEffect(() => {
    let active = true;

    sourceRef.current
      .listMaps()
      .then((resolved) => active && setMaps(resolved))
      .catch(() => active && setMaps([]));

    return () => {
      active = false;
    };
  }, []);

  const finish = useCallback(() => {
    const { projectName: name, clientName: client, emptyResultMessage: empty, onComplete: done } =
      optionsRef.current;

    if (!pages.current.length) {
      setPrinting(false);
      setError(empty || DEFAULT_EMPTY_RESULT);

      return;
    }

    buildSiteMapPdf({ pages: pages.current, projectName: name, clientName: client });
    done();
  }, []);

  // Maps are captured one at a time: each needs its own mounted OpenLayers instance.
  const next = useCallback(() => {
    const nextIndex = indexRef.current + 1;

    if (nextIndex < mapsRef.current.length) {
      indexRef.current = nextIndex;
      dataRef.current = null;
      setData(null);
      setIndex(nextIndex);

      return;
    }

    finish();
  }, [finish]);

  useEffect(() => {
    const doc = mapsRef.current[index];

    if (!printing || !doc) {
      return undefined;
    }

    let active = true;

    captured.current = false;
    sourceRef.current
      .getMapData(doc)
      .then((loaded) => active && setData(loaded))
      // A map with no loadable image is skipped rather than failing the whole print.
      .catch(() => active && next());

    return () => {
      active = false;
    };
    // `next` is stable and `maps` is read through a ref: re-running would restart the map in flight.
  }, [printing, index]);

  const handleInit = useCallback(
    async (map: any) => {
      const doc = mapsRef.current[indexRef.current];
      const mapData = dataRef.current;

      if (captured.current || !doc || !mapData) {
        return;
      }
      captured.current = true;

      try {
        pages.current.push({
          mapDataUrl: await captureMapImage(map, mapData.mapImgSrc),
          mapSize: getCaptureSize(mapData.imageDimensions),
          mapName: doc.name,
        });
        next();
      } catch {
        setPrinting(false);
        captured.current = false;
        setError('Unable to generate the site map PDF.');
      }
    },
    [next],
  );

  const doc = maps?.[index];
  const captureSize = data ? getCaptureSize(data.imageDimensions) : null;

  return {
    maps,
    total: maps?.length || 0,
    index,
    printing,
    error,
    start: useCallback(() => setPrinting(true), []),
    stage:
      printing && data && doc && captureSize ? (
        <div
          key={doc.id}
          style={{
            position: 'fixed',
            left: -20000,
            top: 0,
            width: captureSize.width,
            height: captureSize.height,
          }}
        >
          <MapContext
            mapImgSrc={data.mapImgSrc}
            dataSources={data.dataSources}
            printOnly={true}
            showFeatureLabels={false}
            onInit={handleInit}
            onInitError={() => setError('The site map could not be rendered on this device.')}
          >
            <MapCanvas styles={{ width: '100%', height: '100%' }} />
            {includeEsri && data.esri && (
              <EsriFeaturesLayer
                projectId={data.esri.projectId}
                projectUpload={data.esri.projectUpload}
                rows={data.esri.rows as any}
              />
            )}
          </MapContext>
        </div>
      ) : null,
  };
};
